"use client";

import { AuthUIProvider as BaseAuthUIProvider } from "better-auth-ui";
import type { ComponentProps, ReactNode } from "react";

import { authClient } from "@repo/auth/client";

type BaseAuthUIProviderProps = ComponentProps<typeof BaseAuthUIProvider>;

export type AuthProviderProps = {
  readonly children: ReactNode;
  /** Framework Link component (e.g. a locale-aware Link) for client-side anchors. */
  readonly Link?: BaseAuthUIProviderProps["Link"];
  /** SPA push navigation (e.g. the app's locale-aware `router.push`). */
  readonly navigate?: BaseAuthUIProviderProps["navigate"];
  /**
   * Called after every session change (sign-in, sign-up, sign-out).
   *
   * Pass the framework's cache invalidation here — in Next's App Router that is
   * `router.refresh()`. Without it, the RSC payload the router prefetched for a
   * protected route WHILE SIGNED OUT stays in the client router cache, and it
   * contains that route's redirect to sign-in. The post-sign-up
   * `navigate(redirectTo)` then replays the stale payload and bounces the
   * freshly signed-in user straight back to the sign-in page.
   */
  readonly onSessionChange?: BaseAuthUIProviderProps["onSessionChange"];
  /** Where to send users after a successful sign-in. */
  readonly redirectTo?: BaseAuthUIProviderProps["redirectTo"];
  /** SPA replace navigation (e.g. locale-aware `router.replace`). */
  readonly replace?: BaseAuthUIProviderProps["replace"];
};

export function AuthProvider({
  children,
  Link,
  navigate,
  onSessionChange,
  redirectTo,
  replace,
}: AuthProviderProps) {
  return (
    <BaseAuthUIProvider
      authClient={authClient}
      {...(Link ? { Link } : {})}
      {...(navigate ? { navigate } : {})}
      {...(onSessionChange ? { onSessionChange } : {})}
      {...(replace ? { replace } : {})}
      {...(redirectTo ? { redirectTo } : {})}
    >
      {children}
    </BaseAuthUIProvider>
  );
}
